import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { GET_ITEM_MASTER, BASE_URL, GET_MASTER_DATA, POST_ITEM_MASTER, PUT_ITEM_MASTER, STATUS_CHANGE, GET_MANUFACTURER_MASTER, GET_SUPPLIER_MASTER, DELETE_ITEM_MASTER, POST_SHORT_BOOKS_ITEMS } from '../utlilities/config';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import AddChemicalModal from '../components/AddChemicalModal';
import AddSupplierModal from '../components/AddSupplierModal';

// Icons
import {
  FiSearch,
  FiPlus,
  FiTrash2,
  FiRefreshCw,
  FiDownload,
  FiCheckCircle,
  FiX,
  FiCheck,
  FiFileText,
  FiAlertCircle
} from "react-icons/fi";
import { FaShare } from "react-icons/fa";

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black">
      <FiFileText size={32} />
    </div>
    <h3 className="text-lg font-medium text-black">No products found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search or add a new product</p>
  </div>
);


const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading Product data...</p>
    </div>
  </div>
);

// Share Quantity Modal Component
const ShareQuantityModal = ({ isOpen, onClose, product, qty, setQty, onSubmit }) => {
  const modalRef = useRef(null);
  const qtyInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && qtyInputRef.current) {
      // Focus the quantity input when modal opens
      setTimeout(() => {
        qtyInputRef.current?.focus();
        qtyInputRef.current?.select();
      }, 100);
    }
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent event from bubbling
      onSubmit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-50">
      <div
        className="bg-white rounded-lg shadow-xl w-[90vw] max-w-md outline-none flex flex-col"
        ref={modalRef}
        tabIndex={-1}
      >
        {/* Header */}
        <div className="bg-indigo-600 text-white px-6 py-3 flex items-center justify-between rounded-t-lg">
          <div>
            <h3 className="text-lg font-bold">Add to Short Books</h3>
            <p className="text-sm text-indigo-100 mt-1">{product?.Item_Name || product?.name || 'Product'}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-indigo-700 rounded transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Quantity
            </label>
            <input
              type="text"
              ref={qtyInputRef}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
              placeholder="Enter quantity"
              autoFocus
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t px-6 py-4 flex items-center justify-end gap-3 rounded-b-lg">
          <button
            className="px-5 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSubmit();
            }}
            type="button"
          >
            Add to Short Books
          </button>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, required = false, labelWidthClass = 'w-40', children, actionButton }) => (
  <div className="flex items-start gap-3">
    <div className={`${labelWidthClass} flex items-center justify-between pt-1.5`} style={{width: '200px'}}>
      <label className="text-sm font-medium text-black flex items-center">
        <span>{label}:</span>
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {actionButton && <div className="ml-2">{actionButton}</div>}
    </div>
    <div className="w-full">{children}</div>
  </div>
);

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#f9fafb',
    borderColor: state.isFocused ? '#6366f1' : '#b5b6b8ff',
    boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
    borderRadius: '0.5rem',
    '&:hover': {
      borderColor: '#6366f1',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#92c3fcff'
      : state.isFocused
      ? '#92c3fcff'
      : '#ffffff',
    color: state.isSelected ? '#000000ff' : state.isFocused ? '#ffffff' : '#000000',
    fontWeight: '500',
    fontSize: '0.75rem', // 12px - reduced text size
    padding: '4px 8px', // Reduced padding for smaller text
    '&:hover': {
      backgroundColor: '#92c3fcff',
      color: '#000000ff',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '0.875rem', // 14px - slightly smaller for selected value
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 999,
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

// Rep_Details comes from the API as a JSON string (e.g. "[{\"name\":\"...\",\"offer\":\"...\",\"offer_type\":\"free\"}]")
const parseRepDetails = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((rep) => ({
      name: rep?.name ?? '',
      offer: rep?.offer ?? '',
      offer_type: rep?.offer_type ?? 'free',
    }));
  }
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.map((rep) => ({
          name: rep?.name ?? '',
          offer: rep?.offer ?? '',
          offer_type: rep?.offer_type ?? 'free',
        }))
      : [];
  } catch (error) {
    console.warn('Invalid Rep_Details value:', value);
    return [];
  }
};

const normalizeProductData = (items = []) =>
  items.map((item) => {
    const itemNo = item.Item_No ?? item.ItemNo ?? item.id ?? null;
    const itemName = item.Item_Name ?? item.ItemName ?? '';
    const packageValue = item.package ?? item.Package ?? '';
    const unitValue = item.unit ?? item.Unit ?? '';
    const reorderLevelValue = item.ReorderLevel ?? item.reorderLevel ?? '';
    const onHandValue =
      item.Onhand ?? item.onhand ?? item.TotalStock ?? item.totalStock ?? '';

    return {
      ...item,
      id: item.id ?? itemNo,
      Item_No: itemNo,
      Item_Name: itemName,
      ItemName: item.ItemName ?? itemName,
      package: packageValue,
      Package: item.Package ?? packageValue,
      unit: unitValue,
      Unit: item.Unit ?? unitValue,
      ReorderLevel: reorderLevelValue,
      Discount1: item.Discount1 ?? item.discount1 ?? 0,
      Discount2: item.Discount2 ?? item.discount2 ?? 0,
      Discount3: item.Discount3 ?? item.discount3 ?? 0,
      Supplier: item.Supplier ?? item.supplier ?? '',
      TaxCode: item.TaxCode ?? item.taxCode ?? '',
      Rate: item.Rate ?? item.rate ?? 0,
      MRP: item.MRP ?? item.mrp ?? 0,
      Shelf: item.Shelf ?? item.shelf ?? '',
      Section: item.Section ?? item.section ?? '',
      Category: item.Category ?? item.category ?? '',
      Schedule: item.Schedule ?? item.schedule ?? '',
      ItemType: item.ItemType ?? item.itemType ?? 'Pharmacy',
      Remarks: item.Remarks ?? item.remarks ?? '',
      IsActive: item.IsActive ?? item.isActive ?? true,
      HSNCode: item.HSNCode ?? item.hsnCode ?? '',
      Barcode: item.Barcode ?? item.barcode ?? '',
      Chemical: item.Chemical ?? item.chemical ?? '',
      Rep_Details: item.Rep_Details ?? item.RepDetails ?? item.rep_details ?? '',
      Onhand: onHandValue,
    };
});

const ProductMaster = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [products, setProducts] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState(null);
  const [filterValue, setFilterValue] = useState(null);
  const [showChemicalModal, setShowChemicalModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [chemicalName, setChemicalName] = useState('');
  const [chemicalId, setChemicalId] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareProductId, setShareProductId] = useState(null);
  const [shareQty, setShareQty] = useState('');
  const isSubmittingShareRef = useRef(false); // Prevent double submission
  const token = localStorage.getItem('accessToken');
  const searchDebounceRef = useRef(null);
  const productListRef = useRef(null);
  const productItemRefs = useRef({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(1000);

  useEffect(() => {
    getData();
    getChemicals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);


  // Store all chemicals in a ref to avoid re-renders
  const allChemicalsRef = useRef([]);
  const allCompaniesRef = useRef([]);
  const allSuppliersRef = useRef([]);

  const getChemicals = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MASTER_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      const companyResponse = await axios.get(`${BASE_URL}${GET_MANUFACTURER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      const supplierResponse = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'FilterText': '',

        },
      });

      if (companyResponse.status === 200) {
        const companiesData = companyResponse.data.data || [];
        setCompanies(companiesData);
        allCompaniesRef.current = companiesData;
      }

      if (supplierResponse.status === 200) {
        const suppliersData = supplierResponse.data.data || [];
        setSuppliers(suppliersData);
        allSuppliersRef.current = suppliersData;
      }

      if (response.status === 200) {
        setLoading(false);
        const chemicalsData = (response.data.data || []).filter(item => item.Type === 'C');
        allChemicalsRef.current = chemicalsData;
        // Keep state for backward compatibility (may be used elsewhere)
        setChemicals(chemicalsData);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch labs data:', error);
      toast.error('Could not load labs data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };


  // Initial load - fetch all products
  const getData = async (nextFilters = null) => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_ITEM_MASTER}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
          'PageNo': 1,
          'PageSize': 1000000,
          'FilterText': "",
          'FilterColumn': "0",
          'FilterValue': "0",
        },
      });

      if (response.status === 200) {
        setLoading(false);
        const masterData = response.data.data.masterData || [];
        const pharmacyItems = masterData.filter(item => (item.ItemType ?? item.itemType) === 'Pharmacy');
        const normalizedProducts = normalizeProductData(pharmacyItems);
        console.log('response', normalizedProducts);
        allProductsRef.current = normalizedProducts; // Store all products
        // Apply filters using explicit values when provided (avoids stale state on reset)
        const effectiveSearchQuery = nextFilters ? nextFilters.searchQuery : searchQuery;
        const effectiveFilterType = nextFilters ? nextFilters.filterType : filterType;
        const effectiveFilterValue = nextFilters ? nextFilters.filterValue : filterValue;

        if (effectiveSearchQuery || (effectiveFilterType && effectiveFilterValue)) {
          let filtered = [...normalizedProducts];

          if (effectiveSearchQuery && effectiveSearchQuery.length >= 2) {
            const searchTerm = effectiveSearchQuery.toLowerCase().trim();
            filtered = filtered.filter(product => {
              const itemName = (product.Item_Name || product.ItemName || '').toLowerCase();
              const itemCode = (product.ItemCode || '').toString().toLowerCase();
              return itemName.includes(searchTerm) || itemCode.includes(searchTerm);
            });
          }

          if (effectiveFilterType && effectiveFilterValue) {
            filtered = filtered.filter(product => {
              const productValue = product[effectiveFilterType] || '';
              return String(productValue) === String(effectiveFilterValue);
            });
          }

          setProducts(filtered);
        } else {
          setProducts(normalizedProducts);
        }
        setCurrentPage(1); // Reset to first page when new data is loaded
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch labs data:', error);
      toast.error('Could not load labs data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  // Map filter type to FilterColumn number for API
  // FilterColumn: 1=Chemical, 2=Section, 3=Category, 4=Schedule, 5=Supplier, 6=TaxCode
  const getFilterColumnNumber = (filterType) => {
    const filterColumnMap = {
      'Chemical': 'Chemical',
      'Section': 'Section',
      'Category': 'Category',
      'Schedule': 'Schedule',
      'Supplier': 'Supplier',
      'TaxCode': 'TaxCode',
    };
    return filterColumnMap[filterType] || '0';
  };

  // Store all products (unfiltered) in a ref
  const allProductsRef = useRef([]);

  // Filter products on frontend (no API call needed since we have all products)
  const filterProducts = useCallback(() => {
    let filtered = allProductsRef.current.length > 0 ? [...allProductsRef.current] : [];

    // Apply search filter
    if (searchQuery && searchQuery.length >= 2) {
      const searchTerm = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product => {
        const itemName = (product.Item_Name || product.ItemName || '').toLowerCase();
        const itemCode = (product.ItemCode || '').toString().toLowerCase();
        return itemName.includes(searchTerm) || itemCode.includes(searchTerm);
      });
    }

    // Apply filter type and value
    if (filterType && filterValue) {
      filtered = filtered.filter(product => {
        const productValue = product[filterType] || '';
        return String(productValue) === String(filterValue);
      });
    }

    setProducts(filtered);
    setCurrentPage(1);
  }, [searchQuery, filterType, filterValue]);

  // Find the selected product
  const selectedProduct = selectedProductId ? products.find(p => p.id === selectedProductId) : null;

  // Pagination calculations
  const totalPages = Math.ceil(products.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = products.slice(startIndex, endIndex);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterType, filterValue]);

  // Keyboard navigation for product list
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle arrow keys if user is typing in an input, textarea, or select
      const target = e.target;
      const isInputField = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.tagName === 'SELECT' ||
                          target.isContentEditable;
      
      if (isInputField) {
        return;
      }

      // Only handle ArrowUp and ArrowDown
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        
        if (paginatedData.length === 0) return;

        // Find current selected product index in paginatedData
        const currentIndex = paginatedData.findIndex(p => p.id === selectedProductId);
        
        let newIndex;
        if (e.key === 'ArrowDown') {
          // Move to next item
          if (currentIndex === -1) {
            // No selection, select first item
            newIndex = 0;
          } else if (currentIndex < paginatedData.length - 1) {
            // Move to next item on current page
            newIndex = currentIndex + 1;
          } else if (currentPage < totalPages) {
            // Move to next page
            setCurrentPage(prev => {
              const nextPage = prev + 1;
              // Select first item on next page after state update
              setTimeout(() => {
                const nextPageData = products.slice((nextPage - 1) * itemsPerPage, nextPage * itemsPerPage);
                if (nextPageData.length > 0) {
                  setSelectedProductId(nextPageData[0].id);
                }
              }, 0);
              return nextPage;
            });
            return;
          } else {
            // Already at last item
            return;
          }
        } else {
          // ArrowUp - Move to previous item
          if (currentIndex === -1) {
            // No selection, select last item
            newIndex = paginatedData.length - 1;
          } else if (currentIndex > 0) {
            // Move to previous item on current page
            newIndex = currentIndex - 1;
          } else if (currentPage > 1) {
            // Move to previous page
            setCurrentPage(prev => {
              const prevPage = prev - 1;
              // Select last item on previous page after state update
              setTimeout(() => {
                const prevPageData = products.slice((prevPage - 1) * itemsPerPage, prevPage * itemsPerPage);
                if (prevPageData.length > 0) {
                  setSelectedProductId(prevPageData[prevPageData.length - 1].id);
                }
              }, 0);
              return prevPage;
            });
            return;
          } else {
            // Already at first item
            return;
          }
        }

        // Select the new product
        if (newIndex >= 0 && newIndex < paginatedData.length) {
          const newProduct = paginatedData[newIndex];
          setSelectedProductId(newProduct.id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [paginatedData, selectedProductId, currentPage, totalPages, products, itemsPerPage]);

  // Scroll selected product into view when it changes
  useEffect(() => {
    if (selectedProductId && productItemRefs.current[selectedProductId]) {
      const itemRef = productItemRefs.current[selectedProductId];
      setTimeout(() => {
        if (itemRef && productListRef.current) {
          itemRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    }
  }, [selectedProductId, currentPage]);

  // Refs for form fields
  const codeRef = useRef(null);
  const nameRef = useRef(null);
  const companyRef = useRef(null);
  const chemicalRef = useRef(null);
  const sectionRef = useRef(null);
  const categoryRef = useRef(null);
  const scheduleRef = useRef(null);
  const packageRef = useRef(null);
  const unitRef = useRef(null);
  const mrpRef = useRef(null);
  const rateRef = useRef(null);
  const supplierRef = useRef(null);
  const shelfRef = useRef(null);
  const hsnCodeRef = useRef(null);
  const barcodeRef = useRef(null);
  const taxCodeRef = useRef(null);
  const reorderLevelRef = useRef(null);
  const remarksRef = useRef(null);
  const isActiveRef = useRef(null);
  const discount1Ref = useRef(null);
  const discount2Ref = useRef(null);
  const discount3Ref = useRef(null);
  const saveButtonRef = useRef(null);

  useEffect(() => {
    // Prevent wheel and arrow key issues on number inputs
    const handleWheel = (e) => {
      if (e.target.type === 'number') {
        e.target.blur();
      }
    };

    const handleKeyDown = (e) => {
      if (e.target.type === 'number') {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', handleWheel, { capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const handleAddNew = () => {
    setSelectedProductId(null);
    // Reset form
    setTimeout(() => {
      codeRef.current && codeRef.current.focus();
    }, 100);
  };

  const handleProductClick = (id) => {
    setSelectedProductId(id);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    
    // Clear filter when searching
    if (searchValue && searchValue.length >= 2) {
      setFilterType(null);
      setFilterValue(null);
    }
    
    // Clear previous debounce timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Filter products on frontend with debounce
    searchDebounceRef.current = setTimeout(() => {
      if (searchValue.length === 0) {
        // If search is cleared, apply any active filters or restore all products
        if (filterType && filterValue) {
          filterProducts();
        } else {
          setProducts(allProductsRef.current);
          setCurrentPage(1);
        }
      } else {
        filterProducts();
      }
    }, 300); // 300ms debounce
  };

  const handleToggleActive = async (id, e) => {
    e.stopPropagation(); // Prevent row click
    const product = products.find(p => p.id === id);
    if (!product) return;

    const updatedStatus = !product.IsActive;

    try {
      setLoading(true);
      const response = await axios.post(
        `${BASE_URL}${STATUS_CHANGE}`,
        {},
        {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'ItemNo': id,
            'IsActive': String(updatedStatus),
          },
        }
      );

      if (response.status === 200 || response.status === 204) {
        const updatedProducts = products.map(p =>
          p.id === id ? { ...p, IsActive: updatedStatus } : p
        );
        setProducts(updatedProducts);

        if (selectedProductId === id) {
          setSelectedProductId(null);
          setTimeout(() => setSelectedProductId(id), 0);
        }

        toast.success(
          `${product.Item_Name} is now ${updatedStatus ? 'active' : 'inactive'}.`,
          {
            autoClose: 1000,
            icon: <FiCheckCircle className="text-green-500" />
          }
        );
      } else {
        throw new Error('Failed to update status');
      }
    } catch (error) {
      console.error('Failed to update product status:', error);
      toast.error('Could not change product status.', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };


  const deleteItem = async (id) => {
    setLoading(true);
    console.log('id', id);
    try {
      const response = await axios.post(
        `${BASE_URL}${DELETE_ITEM_MASTER}`,
        {},
        {
          headers: {
            'XApiKey': process.env.REACT_APP_API_KEY,
            'Authorization': `Bearer ${token}`,
            'ItemNo': parseInt(id),
            'IsActive': '',
          },
        }
      );
      if(response.status === 200){
        toast.success('Product successfully deleted.', {
          autoClose: 1000,
          icon: <FiCheckCircle className="text-green-500" />
        });
        getData();
        getChemicals();
      }else{
        throw new Error('Failed to delete product');
      }
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error('Failed to delete product.', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }

  const handleDeleteClick = (id, e) => {
    e.stopPropagation(); // Prevent row click
    Swal.fire({
      title: 'Delete Product?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-1',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-1 mr-2',
        popup: 'rounded-xl shadow-xl'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        deleteItem(id);
      }
    });
  };

  // Handle share button click - open modal for quantity input
  const handleShareClick = useCallback((id, e) => {
    if (e) {
      e.stopPropagation(); // Prevent row click
    }
    const product = products.find(p => p.id === id);
    if (!product) return;
    setShareProductId(id);
    setShareQty('');
    setShowShareModal(true);
  }, [products]);

  // Keyboard shortcut for Alt+G to add selected product to short books
  useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      // Check for Alt+G or Alt+g (case insensitive)
      if (e.altKey && (e.key === 'g' || e.key === 'G')) {
        // Don't trigger if user is typing in an input, textarea, or select
        const target = e.target;
        const isInputField = target.tagName === 'INPUT' || 
                            target.tagName === 'TEXTAREA' || 
                            target.tagName === 'SELECT' ||
                            target.isContentEditable;
        
        if (isInputField) {
          return;
        }

        // Only trigger if a product is selected
        if (selectedProductId) {
          e.preventDefault();
          e.stopPropagation();
          handleShareClick(selectedProductId, null);
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => {
      document.removeEventListener('keydown', handleKeyboardShortcut);
    };
  }, [selectedProductId, handleShareClick]);

  // Handle share API call
  const handleShareSubmit = async () => {
    // Prevent double submission
    if (isSubmittingShareRef.current) {
      return;
    }

    console.log('shareQty', shareQty);
    if (!shareQty || parseFloat(shareQty) <= 0) {
      toast.error('Please enter a valid quantity', { autoClose: 1000 });
      return;
    }

    if (!shareProductId) {
      toast.error('Invalid product selected', { autoClose: 1000 });
      return;
    }

    const product = products.find(p => p.id === shareProductId);
    if (!product || !product.Item_Name) {
      toast.error('Product information is missing', { autoClose: 1000 });
      return;
    }

    // Set flag immediately to prevent double submission
    isSubmittingShareRef.current = true;
    setLoading(true);
    try {
      const payload = [
        {
          itemNo: shareProductId,
          itemName: product.Item_Name || product.ItemName || '',
          qty: shareQty,
          addedAt: new Date().toISOString(),
          isorderd: false
        }
      ];

      const response = await axios.post(`${BASE_URL}${POST_SHORT_BOOKS_ITEMS}`, payload, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.status === 200) {
        toast.success('Item added to short books successfully', { autoClose: 2000 });
        setShowShareModal(false);
        setShareQty('');
        setShareProductId(null);
      } else {
        toast.error('Failed to add item to short books', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Error sharing item:', error);
      toast.error('Failed to add item to short books', { autoClose: 2000 });
    } finally {
      setLoading(false);
      // Reset flag after API call completes
      isSubmittingShareRef.current = false;
    }
  };

  // Handle keyboard navigation between fields
  const handleKeyDown = (e, nextFieldRef, fallbackRef = null) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (nextFieldRef?.current) {
        nextFieldRef.current.focus();
      } else if (fallbackRef?.current) {
        fallbackRef.current.focus();
      }
    }
  };

  // Formik form setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ItemCode: selectedProduct?.ItemCode || '',
      Item_Name: selectedProduct?.Item_Name || '',
      Company: selectedProduct?.Company || '',
      Chemical: selectedProduct?.Chemical || 0,
      Section: selectedProduct?.Section || '',
      Category: selectedProduct?.Category || '',
      Schedule: selectedProduct?.Schedule || '',
      package: selectedProduct?.package ?? '',
      unit: selectedProduct?.unit || '',
      MRP: selectedProduct?.MRP ?? '',
      Rate: selectedProduct?.Rate ?? '',
      Supplier: selectedProduct?.Supplier || 0,
      Shelf: selectedProduct?.Shelf || '',
      HSNCode: selectedProduct?.HSNCode || '',
      Barcode: selectedProduct?.Barcode || '',
      TaxCode: selectedProduct?.TaxCode || '',
      ReorderLevel: selectedProduct?.ReorderLevel ?? '',
      Remarks: selectedProduct?.Remarks || '',
      IsActive: selectedProduct?.IsActive !== undefined ? selectedProduct.IsActive : true,
      Discount1: selectedProduct?.Discount1 ?? '',
      Discount2: selectedProduct?.Discount2 ?? '',
      Discount3: selectedProduct?.Discount3 ?? '',
      TotalStock: selectedProduct?.Onhand ?? '',
      Rep_Details: parseRepDetails(selectedProduct?.Rep_Details),
    },
    validationSchema: Yup.object().shape({
      ItemCode: Yup.string().required('Item code is required'),
      Item_Name: Yup.string().required('Item name is required'),
      Company: Yup.string(),
      Chemical: Yup.string(),
      Section: Yup.string(),
      Category: Yup.string(),
      Schedule: Yup.string(),
      package: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Package must be a number')
        .min(0, 'Package must be positive')
        .nullable(),
      unit: Yup.string(),
      MRP: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('MRP must be a number')
        .min(0, 'MRP must be positive')
        .nullable(),
      Rate: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Rate must be a number')
        .min(0, 'Rate must be positive')
        .nullable(),
      Supplier: Yup.string(),
      Shelf: Yup.string(),
      HSNCode: Yup.string(),
      Barcode: Yup.string(),
      TaxCode: Yup.string(),
      ReorderLevel: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Reorder level must be a number')
        .min(0, 'Reorder level must be positive')
        .nullable(),
      Remarks: Yup.string(),
      IsActive: Yup.boolean(),
      Discount1: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
      Discount2: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
      Discount3: Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .typeError('Discount must be a number')
        .min(0)
        .max(100, 'Discount must be between 0 and 100')
        .nullable(),
      Rep_Details: Yup.array().of(
        Yup.object().shape({
          name: Yup.string(),
          offer: Yup.string(),
          offer_type: Yup.string(),
        })
      ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);

        // Drop empty rows and send as a JSON string, e.g.
        // "[{\"name\":\"Anil Kumar\",\"offer\":\"2 free packs on 10\",\"offer_type\":\"free\"}]"
        const repDetails = (values.Rep_Details || [])
          .filter(rep => (rep.name || '').trim() !== '')
          .map(rep => ({
            name: (rep.name || '').trim(),
            offer: (rep.offer || '').trim(),
            offer_type: rep.offer_type || '',
          }));

        const productData = {
          ItemCode: values.ItemCode,
          Item_Name: values.Item_Name,
          Company: values.Company,
          Chemical: values.Chemical,
          Section: values.Section,
          Category: values.Category,
          Schedule: values.Schedule,
          Package: parseFloat(values.package) || 0,
          Packing_Size: "",
          Unit: values.unit,
          CostPerTest: 0,
          CostPerTestUnit: "",
          Mrp: parseFloat(values.MRP) || 0,
          Rate: parseFloat(values.Rate) || 0,
          Supplier: values.Supplier,
          Shelf: values.Shelf,
          HsnCode: values.HSNCode,
          Barcode: values.Barcode,
          ItemType: 'Pharmacy',
          TaxCode: values.TaxCode,
          ReorderLevel: parseFloat(values.ReorderLevel) || 0,
          ReorderLevelUnit: "",
          Remarks: values.Remarks,
          Discount1: parseFloat(values.Discount1) || 0,
          Discount2: parseFloat(values.Discount2) || 0,
          Discount3: parseFloat(values.Discount3) || 0,
          Rep_Details: JSON.stringify(repDetails),
        };

        console.log(productData);
        if(selectedProductId){
          const response = await axios.post(`${BASE_URL}${PUT_ITEM_MASTER}`, productData,  {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
              'ItemNo': selectedProductId,
            },
          });

          if(response.status === 200){
            setLoading(false);
            toast.success('Product updated successfully.', {
              autoClose: 1000,
              icon: <FiCheckCircle className="text-green-500" />
            });
            handleReset();
          }else{
            setLoading(false);
            toast.error('An error occurred while updating the product.');
          }
        }else{
          const response = await axios.post(`${BASE_URL}${POST_ITEM_MASTER}`, productData,  {
            headers: {
              'XApiKey': process.env.REACT_APP_API_KEY,
              'Authorization': `Bearer ${token}`,
            },
          });
          if(response.status === 200){
            setLoading(false);
            toast.success('Product added successfully.', {
              autoClose: 1000,
              icon: <FiCheckCircle className="text-green-500" />
            });
            handleReset();
          }else{
            setLoading(false);
            toast.error('An error occurred while adding the product.');
          }
        }
      } catch (error) {
        console.error(error);
        toast.error('An error occurred while saving the product.');
      } finally {
        setLoading(false);
      }
    }
  });

  // Rep details (dynamic rows)
  const repDetails = formik.values.Rep_Details || [];

  const handleAddRep = () => {
    formik.setFieldValue('Rep_Details', [
      ...repDetails,
      { name: '', offer: '', offer_type: 'free' },
    ]);
  };

  const handleRemoveRep = (index) => {
    formik.setFieldValue(
      'Rep_Details',
      repDetails.filter((_, i) => i !== index)
    );
  };

  const handleRepChange = (index, field, value) => {
    formik.setFieldValue(
      'Rep_Details',
      repDetails.map((rep, i) => (i === index ? { ...rep, [field]: value } : rep))
    );
  };

  const handleReset = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSelectedProductId(null);
    setSearchQuery('');
    setFilterType(null);
    setFilterValue(null);
    setCurrentPage(1);
    formik.resetForm();
    // Reload initial data
    getData({ searchQuery: '', filterType: null, filterValue: null });
  };

  const handleExportProducts = () => {
    // Export the same list shown in the grid (search + filter), not the full unfiltered ref
    const sourceProducts = products;

    if (!sourceProducts || sourceProducts.length === 0) {
      toast.info('No product data available to export.', {
        autoClose: 1200,
        icon: <FiAlertCircle className="text-blue-500" />
      });
      return;
    }

    const chemicalNameById = new Map(
      (allChemicalsRef.current || []).map(item => [String(item.ID), item.Name || ''])
    );
    const supplierNameById = new Map(
      (allSuppliersRef.current || []).map(item => [String(item.SupplierID), item.SupplierName || ''])
    );

    const resolveChemicalName = (chemicalValue) => {
      if (chemicalValue === null || chemicalValue === undefined || chemicalValue === '') return '';
      const asString = String(chemicalValue);
      return chemicalNameById.get(asString) || asString;
    };

    const resolveSupplierName = (supplierValue) => {
      if (supplierValue === null || supplierValue === undefined || supplierValue === '') return '';
      const asString = String(supplierValue);
      return supplierNameById.get(asString) || asString;
    };

    const exportRows = sourceProducts.map((product, index) => ({
      'S.No': index + 1,
      'Item Code': product.ItemCode || product.Item_No || '',
      'Product Name': product.Item_Name || product.ItemName || '',
      'Chemical': resolveChemicalName(product.Chemical),
      'Supplier': resolveSupplierName(product.Supplier),
      'Rate': Number(product.Rate || 0),
      'MRP': Number(product.MRP || 0),
      'Tax': product.Tax || product.TaxRate || '',
      'Tax Code': product.TaxCode || '',
      'HSN Code': product.HSNCode || '',
      'Section': product.Section || '',
      'Category': product.Category || '',
      'Schedule': product.Schedule || '',
      'Unit': product.Unit || product.unit || '',
      'Reorder Level': product.ReorderLevel || '',
      'Status': product.IsActive ? 'Active' : 'Inactive'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Product Master');

    const now = new Date();
    const datePart = now.toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Product_Master_${datePart}.xlsx`);
  };

  // Handle chemical modal
  const handleOpenChemicalModal = () => {
    setChemicalName('');
    setChemicalId(null);
    setShowChemicalModal(true);
  };

  const handleChemicalSaved = async () => {
    // Refresh chemicals data
    await getChemicals();
    setShowChemicalModal(false);
  };

  // Handle supplier modal
  const handleOpenSupplierModal = () => {
    setSelectedSupplier(null);
    setShowSupplierModal(true);
  };

  const handleSupplierModalClose = () => {
    setShowSupplierModal(false);
    setSelectedSupplier(null);
  };

  const handleSupplierSaved = async () => {
    // Refresh suppliers data
    await getChemicals(); // This also fetches suppliers
    handleSupplierModalClose();
  };

  // Filter products when filter type or value changes
  useEffect(() => {
    filterProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterValue]);


  // Load options for chemical AsyncSelect with filtering
  const loadChemicalOptions = useCallback((inputValue, callback) => {
    // If input is less than 3 characters, return empty to prompt user to type more
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    // Use setTimeout to simulate async behavior and allow React to batch updates
    setTimeout(() => {
      // Filter chemicals based on input
      const filtered = allChemicalsRef.current
        .filter(chemical => 
          chemical.Name && 
          chemical.Name.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 100) // Limit to 100 results for performance
        .map(item => ({
          value: item.ID,
          label: item.Name,
        }));

      callback(filtered);
    }, 100); // Small delay to debounce and improve performance
  }, []);

  // Get selected chemical option for AsyncSelect value prop
  const getSelectedChemicalOption = useCallback((chemicalId) => {
    if (!chemicalId) return null;
    const chemical = allChemicalsRef.current.find(item => item.ID === chemicalId);
    return chemical ? { value: chemical.ID, label: chemical.Name } : null;
  }, []);

  // Load options for supplier AsyncSelect with filtering
  const loadSupplierOptions = useCallback((inputValue, callback) => {
    if (!inputValue || inputValue.length < 2) {
      callback([]);
      return;
    }

    setTimeout(() => {
      const filtered = allSuppliersRef.current
        .filter(supplier => 
          supplier.SupplierName && 
          supplier.SupplierName.toLowerCase().includes(inputValue.toLowerCase())
        )
        .slice(0, 100)
        .map(item => ({
          value: item.SupplierID,
          label: item.SupplierName,
        }));

      callback(filtered);
    }, 100);
  }, []);


  const supplierOptions = suppliers.map(item => ({
    value: item.SupplierID,
    label: item.SupplierName,
  }));

  const companyOptions = companies.map(item => ({
    value:  item.Name,
    label: item.Name,
  }));

  const sectionOptions = [
    { value: 'General', label: 'General' },
    { value: 'Medicine', label: 'Medicine' },
    { value: 'Surgicals', label: 'Surgicals' },
  ];

  const categoryOptions = [
    { value: 'Tablets', label: 'Tablets' },
    { value: 'Capsules', label: 'Capsules' },
    { value: 'Syrups', label: 'Syrups' },
    { value: 'Drops', label: 'Drops' },
    { value: 'Injection', label: 'Injection' },
    { value: 'Cream/gel/ointment', label: 'Cream/gel/ointment' },
    { value: 'Rotacaps', label: 'Rotacaps'},
    { value: 'Inhaler', label: 'Inhaler'},
    { value: 'Orthopaedic item', label: 'Orthopaedic item'},
    { value: 'Generic', label: 'Generic'},
    { value: 'Spray', label: 'Spray'},
    { value: 'Fridge items', label: 'Fridge items'},
    { value: 'Lotion/oil/serum/shampoo/face wash', label: 'Lotion/oil/serum/shampoo/face wash'},
    { value: 'Diapers/napkins', label: 'Diapers/napkins'},
    { value: 'Powder', label: 'Powder'},
    { value: 'Mouth wash', label: 'Mouth wash'},
    { value: 'Soap', label: 'Soap'},
  ];

  const scheduleOptions = [
    { value: 'Schedule H', label: 'Schedule H' },
    { value: 'Schedule H1', label: 'Schedule H1' },
    { value: 'Schedule X', label: 'Schedule X' },
  ];

  const unitOptions = [
    { value: 'Strip', label: 'Strip' },
    { value: 'Bottle', label: 'Bottle' },
    { value: 'Box', label: 'Box' },
  ];

  const taxCodeOptions = [
    { value: '0', label: 'GST 0%' },
    { value: '5', label: 'GST 5%' },
    { value: '12', label: 'GST 12%' },
    { value: '18', label: 'GST 18%' },
    { value: '28', label: 'GST 28%' },
  ];

  const offerTypeOptions = [
    { value: 'free', label: 'Free' },
    { value: 'discount', label: 'Discount %' },
    { value: 'cash', label: 'Cash' },
    { value: 'scheme', label: 'Scheme' },
    { value: 'other', label: 'Other' },
  ];

  const filterTypeOptions = [
    { value: 'Chemical', label: 'Chemical' },
    { value: 'Section', label: 'Section' },
    { value: 'Category', label: 'Category' },
    { value: 'Schedule', label: 'Schedule' },
    { value: 'Supplier', label: 'Supplier' },
    { value: 'TaxCode', label: 'Tax Code' },
  ];

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {/* <LeftMenu isExpanded={isMenuExpanded} toggleMenu={toggleMenu} /> */}
      
      <div className="flex-1 h-full flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200 px-3 py-3 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">
                Product Master
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Filter Type */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-black whitespace-nowrap">Filter Type:</label>
                <div className="w-48">
                  <Select
                    options={filterTypeOptions}
                    placeholder="Filter Type"
                    isClearable
                    isSearchable
                    value={filterTypeOptions.find(option => option.value === filterType) || null}
                    onChange={(selectedOption) => {
                      const newFilterType = selectedOption ? selectedOption.value : null;
                      setFilterType(newFilterType);
                      setFilterValue(null);
                      if (newFilterType) {
                        setSearchQuery('');
                      }
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              {/* Filter Value */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-black whitespace-nowrap">Filter Value:</label>
                <div className="w-48">
                {filterType === 'Chemical' && (
                  <AsyncSelect
                    loadOptions={loadChemicalOptions}
                    placeholder="Search Chemical..."
                    isClearable
                    isSearchable
                    value={getSelectedChemicalOption(filterValue)}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    noOptionsMessage={({ inputValue }) => 
                      inputValue && inputValue.length < 3 
                        ? 'Type at least 3 characters' 
                        : 'No chemicals found'
                    }
                    loadingMessage={() => 'Searching...'}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    defaultOptions={false}
                    cacheOptions={true}
                  />
                )}
                
                {filterType === 'Supplier' && (
                  <AsyncSelect
                    loadOptions={loadSupplierOptions}
                    placeholder="Search Supplier..."
                    isClearable
                    isSearchable
                    value={filterValue ? supplierOptions.find(opt => opt.value === filterValue) || null : null}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    noOptionsMessage={({ inputValue }) => 
                      inputValue && inputValue.length < 2 
                        ? 'Type at least 2 characters' 
                        : 'No suppliers found'
                    }
                    loadingMessage={() => 'Searching...'}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    defaultOptions={false}
                    cacheOptions={true}
                  />
                )}
                
                {filterType === 'Section' && (
                  <Select
                    options={sectionOptions}
                    placeholder="Select Section"
                    isClearable
                    isSearchable
                    value={sectionOptions.find(option => option.value === filterValue) || null}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                
                {filterType === 'Category' && (
                  <Select
                    options={categoryOptions}
                    placeholder="Select Category"
                    isClearable
                    isSearchable
                    value={categoryOptions.find(option => option.value === filterValue) || null}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                
                {filterType === 'Schedule' && (
                  <Select
                    options={scheduleOptions}
                    placeholder="Select Schedule"
                    isClearable
                    isSearchable
                    value={scheduleOptions.find(option => option.value === filterValue) || null}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                
                {filterType === 'TaxCode' && (
                  <Select
                    options={taxCodeOptions}
                    placeholder="Select Tax Code"
                    isClearable
                    isSearchable
                    value={taxCodeOptions.find(option => option.value === filterValue) || null}
                    onChange={(selectedOption) => {
                      setFilterValue(selectedOption ? selectedOption.value : null);
                      if (selectedOption) {
                        setSearchQuery('');
                      }
                    }}
                    styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                )}
                
                {!filterType && (
                  <div className="w-full pl-2 pr-3 py-1 border border-gray-200 bg-gray-100 rounded-lg text-black text-xs flex items-center h-[38px]">
                    Select filter type
                  </div>
                )}
                </div>
              </div>

              {(filterType || filterValue) && (
                <button
                  onClick={() => {
                    setFilterType(null);
                    setFilterValue(null);
                    setSearchQuery('');
                    setProducts(allProductsRef.current);
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center h-[38px]"
                  title="Clear filters"
                >
                  <FiX className="mr-1" size={12} /> Clear
                </button>
              )}

              <button 
                onClick={handleReset}
                className="px-4 py-1.5 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center h-[38px]"
              >
                <FiRefreshCw className="mr-2" size={14} /> Refresh
              </button>
              <button
                onClick={handleExportProducts}
                className="px-4 py-1.5 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center h-[38px]"
              >
                <FiDownload className="mr-2" size={14} /> Export Excel
              </button>
              <button 
                className="px-4 py-1.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center h-[38px]"
                onClick={handleAddNew}
              >
                <FiPlus className="mr-2" /> Add New Product
              </button>
            </div>
          </div>
        </div>
        
        {/* Main Content - Split Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side - Product List */}
          <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col">
            {/* Search Bar */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black">
                  <FiSearch size={16} />
                </div>
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                />
              </div>
              {/* Pagination Info and Controls */}
              {products.length > 0 && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-4 border-t border-gray-200 bg-white">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 rounded-lg border border-gray-200 bg-white text-sm font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded-lg border text-sm font-medium transition-colors ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white border-indigo-600'
                              : 'border-gray-200 bg-white text-black hover:bg-indigo-50 hover:border-indigo-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <span className="text-sm text-black px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 rounded-lg border border-gray-200 bg-white text-sm font-medium text-black disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            
            {/* Product List */}
            <div className="flex-1 overflow-y-auto" ref={productListRef}>
              {products.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {paginatedData.map((product, index) => {
                    const globalIndex = startIndex + index;
                    const onHandRaw = product.Onhand;
                    const onHandDisplay =
                      onHandRaw !== null &&
                      onHandRaw !== undefined &&
                      onHandRaw !== ''
                        ? Number(onHandRaw).toLocaleString(undefined, {
                            maximumFractionDigits: 4,
                          })
                        : '—';
                    return (
                      <div
                        key={product.id ?? product.Item_No}
                        ref={(el) => {
                          if (el && product.id) {
                            productItemRefs.current[product.id] = el;
                          }
                        }}
                        onClick={() => handleProductClick(product.id)}
                        className={`px-4 py-2 cursor-pointer transition-all duration-200 border-b-2 ${
                          selectedProductId === product.id
                            ? 'bg-indigo-50 border-l-4 border-indigo-600'
                            : hoverRow === globalIndex
                            ? 'bg-gray-50'
                            : 'bg-white hover:bg-gray-50'
                        }`}
                        onMouseEnter={() => setHoverRow(globalIndex)}
                        onMouseLeave={() => setHoverRow(null)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0 pr-2">
                            <h3 className="text-sm font-semibold text-black-900 mb-0.5 truncate" title={product.Item_Name}>
                              {product.Item_Name}
                            </h3>
                            <p className="text-xs text-black">
                              On hand: <span className="font-medium text-gray-700 tabular-nums">{onHandDisplay}</span>
                            </p>
                          </div>
                        
                          <div className="flex items-center gap-2">
                            {/* Toggle Switch */}
                            <button
                              type="button"
                              onClick={(e) => handleToggleActive(product.id, e)}
                              className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                product.IsActive ? 'bg-green-600' : 'bg-red-500'
                              }`}
                              title={product.IsActive ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-gray-200 transition-transform ${
                                  product.IsActive ? 'translate-x-5' : 'translate-x-0.5'
                                }`}
                              />
                            </button>
                            <button
                              onClick={(e) => handleShareClick(product.id, e)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Add to short books"
                            >
                              <FaShare size={14} />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(product.id, e)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete product"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <NoData />
              )}
            </div>
          </div>
          
          {/* Right Side - Form */}
          <div className="flex-1 bg-background overflow-y-auto">
            <div className="w-full mx-auto p-6 pt-10">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Form Header */}

                
                {/* Form Content */}
                <form onSubmit={formik.handleSubmit} className="p-6">
                  {loading && (
                    <div className="mb-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-indigo-600"></div>
                      <p className="text-indigo-600 font-medium text-sm">Processing...</p>
                    </div>
                  )}
                  
                  {/* Row 1: Item Code and Name */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Item Code" required>
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="ItemCode"
                              placeholder="Enter Product Code"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.ItemCode && formik.errors.ItemCode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.ItemCode}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={codeRef}
                              onKeyDown={(e) => handleKeyDown(e, nameRef)}
                            />
                          </div>
                          {formik.touched.ItemCode && formik.errors.ItemCode && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.ItemCode}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Item Name" required>
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="Item_Name"
                              placeholder="Enter Product Name"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Item_Name && formik.errors.Item_Name ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Item_Name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={nameRef}
                              onKeyDown={(e) => handleKeyDown(e, companyRef)}
                            />
                          </div>
                          {formik.touched.Item_Name && formik.errors.Item_Name && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Item_Name}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 2: Company and Chemical */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Company">
                        <>
                          <div className="relative">
                            <Select
                              options={companyOptions}
                              placeholder="Select Company"
                              className=""
                              isSearchable
                              isClearable
                              name="Company"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={companyOptions.find(option => option.value === formik.values.Company) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Company', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Company', true)}
                              styles={customSelectStyles}
                              ref={companyRef}

                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = companyRef.current?.state?.menuIsOpen;
                                  const focusedOption = companyRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    chemicalRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Company && formik.errors.Company && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Company}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField 
                        label="Chemical"
                        actionButton={
                          <button
                            type="button"
                            onClick={handleOpenChemicalModal}
                            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                          >
                            <FiPlus size={12} /> New
                          </button>
                        }
                      >
                        <>
                          <div className="relative">
                            <AsyncSelect
                              loadOptions={loadChemicalOptions}
                              placeholder="Select Chemical"
                              className=""
                              isSearchable
                              isClearable
                              name="Chemical"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              defaultOptions={false}
                              cacheOptions={true}
                              value={getSelectedChemicalOption(formik.values.Chemical)}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Chemical', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Chemical', true)}
                              noOptionsMessage={({ inputValue }) => 
                                inputValue.length < 3 
                                  ? 'Enter chemical name' 
                                  : 'No chemicals found'
                              }
                              loadingMessage={() => 'Searching chemicals...'}
                              styles={{
                                ...customSelectStyles,
                                control: (base, state) => ({
                                  ...customSelectStyles.control(base, state),
                                  paddingLeft: '2px'
                                })
                              }}
                              ref={chemicalRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = chemicalRef.current?.state?.menuIsOpen;
                                  const focusedOption = chemicalRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    sectionRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Chemical && formik.errors.Chemical && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Chemical}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>
                  

                  {/* Row 3: Section and Category */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Section">
                        <>
                          <div className="relative">
                            <Select
                              options={sectionOptions}
                              placeholder="Select Section"
                              className=""
                              isSearchable
                              isClearable
                              name="Section"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={sectionOptions.find(option => option.value === formik.values.Section) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Section', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Section', true)}
                              styles={customSelectStyles}
                              ref={sectionRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = sectionRef.current?.state?.menuIsOpen;
                                  const focusedOption = sectionRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    categoryRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Section && formik.errors.Section && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Section}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Category">
                        <>
                          <div className="relative">
                            <Select
                              options={categoryOptions}
                              placeholder="Select Category"
                              className=""
                              isSearchable
                              isClearable
                              name="Category"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={categoryOptions.find(option => option.value === formik.values.Category) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Category', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Category', true)}
                              styles={customSelectStyles}
                              ref={categoryRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = categoryRef.current?.state?.menuIsOpen;
                                  const focusedOption = categoryRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    scheduleRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Category && formik.errors.Category && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Category}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 4: Schedule and Packing Size */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Schedule">
                        <>
                          <div className="relative">
                            <Select
                              options={scheduleOptions}
                              placeholder="Select Schedule"
                              className=""
                              isSearchable
                              isClearable
                              name="Schedule"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={scheduleOptions.find(option => option.value === formik.values.Schedule) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Schedule', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Schedule', true)}
                              styles={customSelectStyles}
                              ref={scheduleRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = scheduleRef.current?.state?.menuIsOpen;
                                  const focusedOption = scheduleRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    packageRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Schedule && formik.errors.Schedule && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Schedule}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Packing Size">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="package"
                              placeholder="Enter Package"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.package && formik.errors.package ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.package}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={packageRef}
                              onKeyDown={(e) => handleKeyDown(e, unitRef)}
                            />
                          </div>
                          {formik.touched.package && formik.errors.package && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.package}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 5: Packing Unit and MRP */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Unit">
                        <>
                          <div className="relative">
                            <Select
                              options={unitOptions}
                              placeholder="Select Unit"
                              className=""
                              isSearchable
                              isClearable
                              name="unit"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={unitOptions.find(option => option.value === formik.values.unit) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('unit', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('unit', true)}
                              styles={customSelectStyles}
                              ref={unitRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = unitRef.current?.state?.menuIsOpen;
                                  const focusedOption = unitRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    mrpRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.unit && formik.errors.unit && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.unit}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="MRP">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="MRP"
                              placeholder="Enter MRP"
                              step="0.01"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.MRP && formik.errors.MRP ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.MRP}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={mrpRef}
                              onKeyDown={(e) => handleKeyDown(e, rateRef)}
                            />
                          </div>
                          {formik.touched.MRP && formik.errors.MRP && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.MRP}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 6: Rate and Supplier */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Rate">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="Rate"
                              placeholder="Enter Rate"
                              step="0.01"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Rate && formik.errors.Rate ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Rate}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={rateRef}
                              onKeyDown={(e) => handleKeyDown(e, supplierRef)}
                            />
                          </div>
                          {formik.touched.Rate && formik.errors.Rate && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Rate}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField 
                        label="Supplier"
                        actionButton={
                          <button
                            type="button"
                            onClick={handleOpenSupplierModal}
                            className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                          >
                            <FiPlus size={12} /> New
                          </button>
                        }
                      >
                        <>
                          <div className="relative">
                            <Select
                              options={supplierOptions}
                              placeholder="Select Supplier"
                              className=""
                              isSearchable
                              isClearable
                              name="Supplier"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={supplierOptions.find(option => option.value === formik.values.Supplier) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('Supplier', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('Supplier', true)}
                              styles={customSelectStyles}
                              ref={supplierRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = supplierRef.current?.state?.menuIsOpen;
                                  const focusedOption = supplierRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    shelfRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.Supplier && formik.errors.Supplier && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Supplier}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 7: Shelf and HSN Code */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Shelf">
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="Shelf"
                              placeholder="Enter Shelf"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Shelf && formik.errors.Shelf ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Shelf}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={shelfRef}
                              onKeyDown={(e) => handleKeyDown(e, hsnCodeRef)}
                            />
                          </div>
                          {formik.touched.Shelf && formik.errors.Shelf && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Shelf}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="HSN Code">
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="HSNCode"
                              placeholder="Enter HSN Code"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.HSNCode && formik.errors.HSNCode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.HSNCode}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={hsnCodeRef}
                              onKeyDown={(e) => handleKeyDown(e, barcodeRef)}
                            />
                          </div>
                          {formik.touched.HSNCode && formik.errors.HSNCode && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.HSNCode}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Barcode Number">
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="Barcode"
                              placeholder="Enter Barcode Number"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Barcode && formik.errors.Barcode ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Barcode}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={barcodeRef}
                              onKeyDown={(e) => handleKeyDown(e, taxCodeRef)}
                            />
                          </div>
                          {formik.touched.Barcode && formik.errors.Barcode && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Barcode}</p>
                          )}
                        </>
                      </FormField>
                    </div>


                    <div>
                      <FormField label="Total Stock">
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="TotalStock"
                              placeholder="Enter Total Stock"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Onhand && formik.errors.Onhand ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.TotalStock}
                              readOnly
                            />
                          </div>
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 9: Tax Code and Reorder Level */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Tax Code">
                        <>
                          <div className="relative">
                            <Select
                              options={taxCodeOptions}
                              placeholder="Select Tax Code"
                              className=""
                              isSearchable
                              isClearable
                              name="TaxCode"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              value={taxCodeOptions.find(option => option.value === formik.values.TaxCode) || null}
                              onChange={(selectedOption) => {
                                formik.setFieldValue('TaxCode', selectedOption ? selectedOption.value : '');
                              }}
                              onBlur={() => formik.setFieldTouched('TaxCode', true)}
                              styles={customSelectStyles}
                              ref={taxCodeRef}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  const isMenuOpen = taxCodeRef.current?.state?.menuIsOpen;
                                  const focusedOption = taxCodeRef.current?.state?.focusedOption;
                                  
                                  if (!isMenuOpen && !focusedOption) {
                                    e.preventDefault();
                                    reorderLevelRef.current?.focus()
                                  } 
                                }
                              }}
                            />
                          </div>
                          {formik.touched.TaxCode && formik.errors.TaxCode && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.TaxCode}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Reorder Level">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="ReorderLevel"
                              placeholder="Enter Reorder Level"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.ReorderLevel && formik.errors.ReorderLevel ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.ReorderLevel}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={reorderLevelRef}
                              onKeyDown={(e) => handleKeyDown(e, remarksRef)}
                            />
                          </div>
                          {formik.touched.ReorderLevel && formik.errors.ReorderLevel && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.ReorderLevel}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 10: Remarks and Is Active */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <FormField label="Remarks">
                        <>
                          <div className="relative">
                            <input
                              type="text"
                              name="Remarks"
                              placeholder="Enter Remarks"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Remarks && formik.errors.Remarks ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Remarks}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={remarksRef}
                              onKeyDown={(e) => handleKeyDown(e, isActiveRef)}
                            />
                          </div>
                          {formik.touched.Remarks && formik.errors.Remarks && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Remarks}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Is Active">
                        <>
                          <div className="relative">
                            <select
                              name="IsActive"
                              className={`w-full pl-2 pr-2 py-1 border ${
                                formik.touched.IsActive && formik.errors.IsActive ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors appearance-none`}
                              value={formik.values.IsActive ? 'true' : 'false'}
                              onChange={(e) => formik.setFieldValue('IsActive', e.target.value === 'true')}
                              onBlur={formik.handleBlur}
                              ref={isActiveRef}
                              onKeyDown={(e) => handleKeyDown(e, discount1Ref)}
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-black">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                              </svg>
                            </div>
                          </div>
                          {formik.touched.IsActive && formik.errors.IsActive && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.IsActive}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 11: Discounts */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div>
                      <FormField label="Discount %1" labelWidthClass="w-32">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="Discount1"
                              placeholder="Enter Discount %1"
                              step="0.01"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Discount1 && formik.errors.Discount1 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Discount1}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={discount1Ref}
                              onKeyDown={(e) => handleKeyDown(e, discount2Ref)}
                            />
                          </div>
                          {formik.touched.Discount1 && formik.errors.Discount1 && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Discount1}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Discount %2" labelWidthClass="w-32">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="Discount2"
                              placeholder="Enter Discount %2"
                              step="0.01"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Discount2 && formik.errors.Discount2 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Discount2}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={discount2Ref}
                              onKeyDown={(e) => handleKeyDown(e, discount3Ref)}
                            />
                          </div>
                          {formik.touched.Discount2 && formik.errors.Discount2 && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Discount2}</p>
                          )}
                        </>
                      </FormField>
                    </div>

                    <div>
                      <FormField label="Discount %3" labelWidthClass="w-32">
                        <>
                          <div className="relative">
                            <input
                              type="number"
                              name="Discount3"
                              placeholder="Enter Discount %3"
                              step="0.01"
                              className={`w-full pl-2 pr-3 py-1 border ${
                                formik.touched.Discount3 && formik.errors.Discount3 ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-200'
                              } bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors`}
                              value={formik.values.Discount3}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              ref={discount3Ref}
                              onKeyDown={(e) => handleKeyDown(e, saveButtonRef)}
                            />
                          </div>
                          {formik.touched.Discount3 && formik.errors.Discount3 && (
                            <p className="mt-1 text-xs text-red-500">{formik.errors.Discount3}</p>
                          )}
                        </>
                      </FormField>
                    </div>
                  </div>

                  {/* Row 12: Rep Details (dynamic) */}
                  <div className="mb-6 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-medium text-black">Rep Details:</label>
                      <button
                        type="button"
                        onClick={handleAddRep}
                        className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                      >
                        <FiPlus size={12} /> Add Rep
                      </button>
                    </div>

                    {repDetails.length === 0 ? (
                      <p className="text-xs text-gray-500">No rep details added. Click "Add Rep" to add one.</p>
                    ) : (
                      <div className="space-y-2">
                        {repDetails.map((rep, index) => (
                          <div key={index} className="grid grid-cols-12 gap-2 items-center">
                            <div className="col-span-4">
                              <input
                                type="text"
                                placeholder="Rep Name"
                                className="w-full pl-2 pr-3 py-1 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors"
                                value={rep.name}
                                onChange={(e) => handleRepChange(index, 'name', e.target.value)}
                              />
                            </div>
                            <div className="col-span-5">
                              <input
                                type="text"
                                placeholder="Offer (e.g. 2 free packs on 10)"
                                className="w-full pl-2 pr-3 py-1 border border-gray-200 focus:border-indigo-500 focus:ring-indigo-200 bg-gray-50 rounded-lg focus:ring-2 focus:outline-none transition-colors"
                                value={rep.offer}
                                onChange={(e) => handleRepChange(index, 'offer', e.target.value)}
                              />
                            </div>
                            <div className="col-span-2">
                              <Select
                                options={offerTypeOptions}
                                placeholder="Offer Type"
                                isSearchable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                value={offerTypeOptions.find(option => option.value === rep.offer_type) || null}
                                onChange={(selectedOption) =>
                                  handleRepChange(index, 'offer_type', selectedOption ? selectedOption.value : '')
                                }
                                styles={customSelectStyles}
                              />
                            </div>
                            <div className="col-span-1 flex justify-center">
                              <button
                                type="button"
                                onClick={() => handleRemoveRep(index)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove rep"
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      className="px-6 py-1 text-sm font-medium text-black bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-200 transition-colors flex items-center"
                      onClick={handleReset}
                    >
                      <FiX className="mr-2" /> Reset
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-1 text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 transition-all shadow-md hover:shadow-lg flex items-center"
                      ref={saveButtonRef}
                      disabled={loading}
                    >
                      <FiCheck className="mr-2" /> {selectedProductId ? 'Update Product' : 'Save Product'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Chemical Modal */}
      {showChemicalModal && (
        <AddChemicalModal
          setShowModal={setShowChemicalModal}
          chemicalName={chemicalName}
          onSave={handleChemicalSaved}
          chemicalId={chemicalId}
          setChemicalName={setChemicalName}
          setSelectedChemicalId={setChemicalId}
        />
      )}

      {/* Supplier Modal */}
      {showSupplierModal && (
        <AddSupplierModal
          setShowModal={handleSupplierModalClose}
          onSave={handleSupplierSaved}
          selectedItem={selectedSupplier}
          setSelectedItem={setSelectedSupplier}
          mode="Supplier"
        />
      )}

      {/* Share Quantity Modal */}
      <ShareQuantityModal
        isOpen={showShareModal}
        onClose={() => {
          setShowShareModal(false);
          setShareQty('');
          setShareProductId(null);
          // Reset submission flag when modal closes
          isSubmittingShareRef.current = false;
        }}
        product={shareProductId ? products.find(p => p.id === shareProductId) : null}
        qty={shareQty}
        setQty={setShareQty}
        onSubmit={handleShareSubmit}
      />
    </div>
  );
};

export default withAuth(ProductMaster);
